import classNames from 'classnames';
import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import Image from 'utils/image';
import { formatMoney } from 'utils/storefront/format-resource';
import { withCartContext } from './cart-context';

const CartItem = (props) => {
  const { cart, line, index } = props;
  const [qtyError, setQtyError] = useState(false);
  let maxQuantity =
    line.merchandise?.quantityAvailable ||
    line.quantity_rule?.maxQuantity ||
    line.properties?._inventory_quantity ||
    Infinity;

  const isGwp = line?.properties?._gwp || line.discounted_price === 0;

  if (line.children) {
    // If bundle, get lowest child quantity
    maxQuantity = line.children.reduce((prevQuantity, child) => {
      const childMax =
        (cart.mode === 'storefront'
          ? child.merchandise?.quantityAvailable
          : child.quantity_rule?.maxQuantity) || Infinity;
      return childMax < prevQuantity ? childMax : prevQuantity;
    }, Infinity);
  }

  const handleAdjustQuantity = (e, quantity) => {
    const qty = quantity > maxQuantity ? maxQuantity : quantity;
    if (quantity >= maxQuantity) setQtyError(true);
    else setQtyError(false);
    const plusButton = document.querySelector('[data-cart-incrementor="plus"]');
    const incrementDirection = plusButton.dataset.cartIncrementor;

    let payload = [
      {
        id: line.key,
        quantity: qty,
      },
    ];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => ({
        id: child.id,
        attributes: child.attributes,
        quantity: qty,
      }));
    }

    return cart.update(payload).then(() => {
      if (incrementDirection === 'plus' && maxQuantity <= quantity) {
        e.target.classList.add('text-gray-700');
      } else {
        plusButton.classList.remove('text-gray-700');
      }
    });
  };

  const handleRemove = () => {
    if (index === 0) {
      localStorage.setItem('upsellProductData', null);
    }

    let payload = [line.key];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => child.id);
    }

    return cart.remove(payload);
  };

  const lineItem = useMemo(
    () => (
      <div
        className="order-3 mb-12 flex gap-16 px-20 first-of-type:order-1 group-first-of-type:order-1 lg:mb-16"
        key={line.id}
      >
        <div className="min-w-[117px] max-w-[117px] h-[146px]">
          <a
            href={
              cart.mode === 'storefront'
                ? `/products/${line.merchandise.product.handle}`
                : line.url
            }
          >
            <Image
              classes="h-full object-cover w-full"
              src={
                cart.mode === 'storefront'
                  ? line.merchandise.product?.featuredImage?.url
                  : line.featured_image?.url
              }
              srcWidths={{ sm: 200 }}
            />
          </a>
        </div>
        <div className="grid w-full grid-cols-4 grid-rows-[1fr_auto] gap-y-30">
          <div className="col-span-3">
            <div className="mb-[7px]">
              <a
                href={
                  cart.mode === 'storefront'
                    ? `/products/${line.merchandise.product.handle}`
                    : line.url
                }
                className="cta-medium lg:eyebrow inline-block"
              >
                {cart.mode === 'storefront'
                  ? line.merchandise?.product.title
                  : line.product_title}
              </a>
            </div>
            {line.options_with_values?.map((option, indexs) => (
              <p
                key={indexs}
                className="body-3 mb-4 text-subdued"
              >
                {option.name}: {option.value}
              </p>
            ))}
            {qtyError && (
              <p className="body-3 mb-1.5 text-error lg:mb-1">
                {window.TMW.Strings.cart.qtyError}
              </p>
            )}
          </div>
          <p className="body-3 col-span-1 flex justify-end gap-1 pt-4 text-right text-gray-900">
            {isGwp ? (
              <span className="body-3 uppercase">Free</span>
            ) : (
              <span className={`body-3`}>
                {formatMoney(
                  line?.cost?.totalAmount ||
                    (line?.final_line_price && {
                      amount: line.final_line_price / 100,
                    }),
                )}
              </span>
            )}
          </p>
          {!isGwp && (
            <div className="relative col-span-3 flex h-40 w-full max-w-[101px] items-center rounded-[20px] border border-default p-0 px-18 text-center">
              <button
                onClick={(e) => handleAdjustQuantity(e, line.quantity - 1)}
                aria-label={window.TMW.Strings.cart.remove}
                data-cart-incrementor="minus"
              >
                &#8722;
              </button>
              <input
                type="number"
                value={line.quantity}
                onBlur={(e) =>
                  handleAdjustQuantity(e, parseInt(e.target.value, 10))
                }
                onInput={(e) =>
                  handleAdjustQuantity(e, parseInt(e.target.value, 10))
                }
                max={maxQuantity}
                className="no-spinner body-1 h-40 w-full border-none bg-transparent p-0 text-center text-gray-900 focus:ring-0"
              />
              <button
                className={maxQuantity <= line.quantity ? 'text-gray-700' : ''}
                onClick={(e) => handleAdjustQuantity(e, line.quantity + 1)}
                aria-label={window.TMW.Strings.cart.add}
                data-cart-incrementor="plus"
              >
                &#43;
              </button>
            </div>
          )}
          <button
            onClick={() => handleRemove()}
            className={classNames(
              'body-3 h-fit self-end text-right text-gray-800 underline underline-offset-4',
              isGwp ? 'col-span-4' : '',
            )}
          >
            {window.TMW.Strings.cart.remove}
          </button>
        </div>
      </div>
    ),
    [line],
  );

  return lineItem;
};

export default withCartContext(CartItem);
